import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, FormControlLabel, Switch, CircularProgress, Alert, Box } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ImpactAnalysisPanel } from './ImpactAnalysisPanel';
import { fetchApi } from '@/api/fetchApi';

export interface RevokeEntitlementDialogProps {
  tenantId: string;
  domainId: string;
  domainName: string;
  open: boolean;
  onClose: () => void;
  onRevoked?: () => void;
}

export function RevokeEntitlementDialog({ tenantId, domainId, domainName, open, onClose, onRevoked }: RevokeEntitlementDialogProps) {
  const [force, setForce] = useState(false);
  const queryClient = useQueryClient();

  const revokeMutation = useMutation({
    mutationFn: () => fetchApi(`/domains/tenants/${tenantId}/entitlements/revoke/${domainId}`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entitlements', tenantId] });
      onRevoked?.();
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Revoke Entitlement</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Revoke <strong>{domainName}</strong> ({domainId}) for this tenant?
        </Typography>

        <ImpactAnalysisPanel domainId={domainId} action="disable" tenantId={tenantId} />

        <FormControlLabel
          control={<Switch checked={force} onChange={(e) => setForce(e.target.checked)} />}
          label="Force revoke (skip dependent check)"
          sx={{ mt: 2 }}
        />

        {revokeMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>Revoke failed. Please try again.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => revokeMutation.mutate()}
          disabled={revokeMutation.isPending}
          startIcon={revokeMutation.isPending ? <CircularProgress size={16} /> : <Warning />}
        >
          Revoke
        </Button>
      </DialogActions>
    </Dialog>
  );
}
